import type { ResultNode } from "../engine";
import type { ProfessionalDetails } from "../ui/referral";

export interface Requester {
  name: string;
  email: string;
  phone: string;
  relationship: string;
}

export interface SubmitInput {
  endpoint: string;
  pdfBlob: Blob;
  condition: string;
  determination: ResultNode["determination"];
  requester: Requester;
  consent: boolean;
  turnstileToken: string;
  idempotencyKey: string;
  professional?: ProfessionalDetails;
}

export type SubmitResult =
  | { status: "success"; referenceId: string }
  | { status: "error"; message: string };

// Encodes a Blob to bare base64 (no "data:...;base64," prefix), which is what the relay expects.
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read PDF"));
    reader.readAsDataURL(blob);
  });
}

// A stable per-submission key so retries do not double-send. crypto.randomUUID is available in
// modern browsers and jsdom; fall back to a timestamped random string if absent.
export function newIdempotencyKey(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `idem-${Math.random().toString(36).slice(2)}-${performance.now().toString(36)}`;
}

// Keep only the non-empty identifier fields so the record and the downstream CSV stay clean.
function compactProfessional(p: ProfessionalDetails): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) {
    if (typeof v === "string" && v.trim().length > 0) out[k] = v.trim();
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export async function submitAssessment(input: SubmitInput): Promise<SubmitResult> {
  if (!input.endpoint) {
    return { status: "error", message: "No submission endpoint is configured." };
  }
  try {
    const pdfBase64 = await blobToBase64(input.pdfBlob);
    const professional = input.professional ? compactProfessional(input.professional) : undefined;
    const body = {
      pdfBase64,
      condition: input.condition,
      determination: input.determination,
      requester: input.requester,
      consent: input.consent,
      idempotencyKey: input.idempotencyKey,
      turnstileToken: input.turnstileToken,
      ...(professional ? { professional } : {}),
    };
    const response = await fetch(input.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      return { status: "error", message: `Submission failed (${response.status}).` };
    }
    const data = (await response.json()) as { ok?: boolean; referenceId?: string };
    if (!data.ok) {
      return { status: "error", message: "Submission was not accepted." };
    }
    return { status: "success", referenceId: data.referenceId ?? "" };
  } catch {
    return { status: "error", message: "Could not reach the submission service." };
  }
}
